import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { getColumns } from "./tableconfig";
import { PropTypes } from "prop-types";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import TableStore from "@harbor/elements/utils/table/store";
import { ad_circuit_summary_TablePayload } from "../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../config/api-config";
import { downloadAsCSVFile } from "../../../../../../utils/common";
import css from "../../common/anomalyMagneticStyle.less";
import CustomTimeStamp from "../../../../../../generics/TimeStamp/customTimeStamp";
import { addUnitsFromDate } from "../../../../../../utils/displayTime";
import ADTableView from "../../genericComponents/ADTableView";
import Spinner from "../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../common/ErrorComponent";
import { generateRowID, payloadOnQueryUpdate } from "../../common/adUtils";
import { useNavigate } from "react-router-dom";
import useI18n from "../../../../../../i18n/useI18n";
import { AD_USAGE_ANOMALY_SUMMARY_TABLE_DEFAULT_TGL, LOCAL_STORAGE_LABELS } from "../../../../../../utils/enums";
import { useTableSettings } from "../../../../../../utils/table";
let defaultSort = [["display_circuit_usage_col", "desc"]];
let defaultRowID = "row-id_0";
const tableSettingsId = "ad_usage_anomaly_summary_table";

const AdSummaryTable = props => {
  const { tViews } = useI18n('views')
  const navigate = useNavigate();
  const latestRequest = useRef(0);
  const tableStoreRef = useRef(null);
  const [tableStore, setTableStore] = useState(null);
  const [tableLoader, setTableLoader] = useState(true);
  const [tableError, setTableError] = useState(null);
  const [selection, setSelection] = useState(defaultRowID);

  const openTableSettings = useCallback(() => {
    tableStoreRef.current?.setState({ settingsOpen: true });
  }, []);

  let columns = useMemo(
    () =>
      getColumns(
        props.saveUsageCipProps,
        setSelection,
        selection,
        props.setSelectedSite,
        props.circuitsMasterTable,
        navigate,
        openTableSettings
      ),
    [selection, props.circuitsMasterTable, navigate, openTableSettings]
  );

  const columnsHook = useColumns({
    config: () => columns,
    order: defaultSort
  });

  useEffect(() => {
    const abortController = new AbortController();
    latestRequest.current += 1;
    const requestId = latestRequest.current;

    const fetchData = async () => {
      try {
        // Fetch data logic
        setSelection(defaultRowID);
        let tableStore = new TableStore({
          query: { pageLength: 30 },
          hooks: [columnsHook],
          fetchData: async ({ query, setSource, setCount }) => {
            if (!query.export) setTableLoader(true);
            let payload = ad_circuit_summary_TablePayload(props.selectedHour);
            payload = payloadOnQueryUpdate(query, payload);
            if (props.selectedTab === "anomalyCellularTab")
              payload["anomaly_flag"] = true;
            let res = await ApiService.getADCircuitSummaryTable(payload, {
              signal: abortController.signal
            });
            if (latestRequest.current !== requestId) return;
            if (!res.errorObject && !query.export) {
              let tableData = generateRowID(res?.data?.data, true);
              props.saveUsageCipProps({
                circuitName: tableData[0]?.circuit_name,
                site: tableData[0]?.site_id,
                displayCircuitName: tableData[0]?.display_circuit_name,
                displaySiteName: tableData[0]?.site_name
              });
              setSource(tableData);
              setCount(res?.data?.count, false);
              setTableLoader(false);
            } else if (query.export) {
              let resData = res?.data?.data;
              const exportColumns = tableStore.getState().columns
                .filter(item => item.prop !== "actions");
              const data = [exportColumns.map(item => `"${item.name}"`).join(",")];
              for (const item of resData) {
                data.push(
                  exportColumns
                    .map(col => `"${item[col.prop] ?? ""}"`)
                    .join(",")
                );
              }
              downloadAsCSVFile(data.join("\n"), tViews('anomalySummary'));
              delete query.export;
              setTableLoader(false);
            } else {
              setTableError(res.errorObject);
              setTableLoader(false);
            }
          }
        });
        tableStoreRef.current = tableStore;
        useTableSettings(
          LOCAL_STORAGE_LABELS.AD_USAGE_ANOMALY_SUMMARY_TABLE,
          AD_USAGE_ANOMALY_SUMMARY_TABLE_DEFAULT_TGL,
          columnsHook,
          tableStore
        );
        setTableStore(tableStore);
      } catch (error) {
        // Handle error
        if (error.name === "AbortError") return; // Ignore abort error
        // Only process if this is the latest request
        if (latestRequest.current !== requestId) return;
        setTableError(error.message);
        setTableLoader(false);
      }
    };
    if (props.selectedHour) fetchData();
    return () => {
      abortController.abort();
    };
  }, [props.selectedHour, props.refresh, props.selectedTab]);

  useEffect(() => {
    return () => {
      props.saveUsageCipProps({});
    };
  }, []);

  return (
    <div>
      {tableStore ? (
        <div
          className={css["ad-circuit-summary-table"]}
          data-cy="p-netw-ad-cs-table"
          data-testid="p-netw-ad-cs-table"
          aria-label="Anomaly Summary Table"
        >
          <div className="customt-timestamp">
            <CustomTimeStamp
              prevTime={props.selectedHour}
              currentTime={addUnitsFromDate(
                new Date(props.selectedHour),
                1,
                "h"
              )}
            />
          </div>
          <ADTableView
            tableStore={tableStore}
            header={
              props.selectedTab === "anomalyCellularTab"
                ? tViews('predictiveNetworks.anomalySummaryLabel')
                : tViews('predictiveNetworks.cellularSummaryLabel')
            }
            columns={tableStore?.getState()?.columns}
            id={"ad-cs-table"}
            status={{ isLoading: tableLoader, error: tableError }}
            defaultSort={defaultSort}
            isPagination={
              props.selectedTab === "anomalyCellularTab"
                ? false
                : true
            }
            entity={tViews('predictiveNetworks.circuitsSmall')}
            message={tViews('predictiveNetworks.noAnomalousCircuits')}
            tableSettings={{
              localStorageName: LOCAL_STORAGE_LABELS.AD_USAGE_ANOMALY_SUMMARY_TABLE,
              defaultToggles: AD_USAGE_ANOMALY_SUMMARY_TABLE_DEFAULT_TGL,
              dataTestid: tableSettingsId
            }}
          />
        </div>
      ) : tableLoader ? (
        <Spinner />
      ) : (
        tableError && <ErrorComponent role="alert" />
      )}
    </div>
  );
};

AdSummaryTable.propTypes = {
  selectedHour: PropTypes.string.isRequired,
  refresh: PropTypes.bool,
  saveUsageCipProps: PropTypes.func.isRequired,
  setSelectedSite: PropTypes.func.isRequired,
  circuitsMasterTable: PropTypes.object.isRequired,
  selectedTab: PropTypes.string.isRequired
};

export default AdSummaryTable;
